import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { Avatar } from '@sketchnote/shared-components';
import * as React from 'react';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';

import { useTasksStore } from '../store/useTasksStore';
const { Fragment, useCallback } = React;

export const EditItemAssigner = ({ members }: { members: any }) => {
  const taskId = useTasksStore((s) => s.currentTaskId);
  const onUpdateItemField = useTasksStore((s) => s.onUpdateItemField);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>();

  const [search, setSearch] = React.useState<string>('');
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setSearch('');
  }, [isPopoverOpen]);

  const item = useTasksStore(
    useCallback(
      (s) => {
        if (taskId) {
          return s.tasks[taskId];
        }
      },
      [taskId]
    )
  );

  const { isReadOnly } = useTaskAccess(taskId || '');

  const assignerValue = React.useMemo(() => {
    if (item?.assigner) {
      const assigner = members?.find((obj) => obj.memberId === item.assigner);
      if (assigner)
        return {
          label: `${assigner?.firstname}`,
          firstname: assigner?.firstname,
          email: assigner?.email,
          lastname: assigner?.lastname,
          avatar: assigner?.avatar,
          memberId: assigner?.memberId,
          value: assigner?.memberId,
        };
    }
    return undefined;
  }, [item?.assigner, members]);

  const handleChangeAssigner = (value) => {
    onUpdateItemField({ item: { assigner: value, id: item?.id }, keys: ['assigner'] });
    setIsPopoverOpen(false);
  };

  const membersOptions = React.useMemo(
    () =>
      (
        members?.map((obj) => ({
          label: obj.firstname,
          value: obj.memberId,
          firstname: obj.firstname,
          email: obj.email,
          lastname: obj.lastname,
          memberId: obj.memberId,
          avatar: obj.avatar,
        })) || []
      ).filter(
        (m) =>
          m.label.toLowerCase().includes(search.toLowerCase()) ||
          m.email.toLowerCase().includes(search.toLowerCase())
      ),
    [members, search]
  );

  if (!item) return <></>;

  return (
    <Fragment>
      <Popover isOpen={!isReadOnly && isPopoverOpen} onClose={() => setIsPopoverOpen(false)}>
        <PopoverTrigger>
          <button
            className="flex items-center"
            onClick={() => {
              setIsPopoverOpen(true);
              setTimeout(() => {
                searchInputRef.current?.focus();
              }, 100);
            }}
          >
            {assignerValue ? (
              <Avatar
                size="sm1"
                src={assignerValue.avatar}
                type="photo"
                name={assignerValue.label}
              />
            ) : (
              <Avatar
                size="sm1"
                type="add-member"
                name={
                  isReadOnly
                    ? 'You do not have the required permission to edit tasks'
                    : 'Add assigner'
                }
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent>
          <div className="bg-white rounded-lg shadow-md">
            <div
              style={{
                maxHeight: '300px',
                width: '220px',
                overflow: 'auto',
              }}
            >
              <span className="">
                <input
                  className="text-gray-600 text-sm placeholder-gray-500 px-8 py-4 border-b border-gray-200 w-full"
                  ref={searchInputRef}
                  placeholder="Search"
                  onChange={(e) => setSearch(e.target.value)}
                  value={search}
                />
              </span>
              {membersOptions.map((obj) => {
                return (
                  <div
                    key={obj.memberId}
                    className="flex items-center gap-12 cursor-pointer px-12 py-6 hover:bg-gray-50"
                    onClick={() => handleChangeAssigner(obj.memberId)}
                  >
                    <span className="flex-shrink-0">
                      <Avatar src={obj.avatar} type="photo" name={obj.label} />
                    </span>
                    <div className="flex flex-col flex-grow min-w-0">
                      <div className="text-sm text-gray-500">{obj.label}</div>
                      <div className="text-xs text-gray-400 truncate w-full">{obj.email}</div>
                    </div>
                  </div>
                );
              })}
              {!membersOptions?.length && (
                <div className="text-sm text-gray-500 text-center px-10 py-6">No users found</div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </Fragment>
  );
};
